import { CHAIN_ID } from '@derivation-tech/context';
import { ERC20, ERC20__factory } from '@derivation-tech/contracts';
import { JsonRpcProvider, JsonRpcSigner } from '@ethersproject/providers';
import { Instrument, Instrument__factory } from '@synfutures/sdks-perp/dist/typechain';
import { useMemo } from 'react';
import type { Address } from 'viem';
import { getContract } from 'viem';

import { isTestnet } from '@/utils/chain';
import { useProviderOrSigner } from './useChain';
import { useWalletPublicClient, useWalletViemClient } from './useWalletNetwork';

// ERC20 ABI for viem (basic functions)
const ERC20_ABI = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    type: 'function',
    name: 'transfer',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
] as const;

// New viem-based contract hooks
export function useViemErc20Contract(tokenAddr: string | undefined) {
  const publicClient = useWalletPublicClient();
  const walletClient = useWalletViemClient();

  return useMemo(() => {
    if (!tokenAddr || !publicClient) return undefined;

    return getContract({
      address: tokenAddr as Address,
      abi: ERC20_ABI,
      client: {
        public: publicClient,
        wallet: walletClient,
      },
    });
  }, [tokenAddr, publicClient, walletClient]);
}

// Legacy ethers-based contract hooks (maintained for backward compatibility)
export function useErc20Contract(tokenAddr: string | undefined, signer: JsonRpcSigner | undefined): ERC20 | undefined {
  return useMemo(() => {
    if (signer && tokenAddr) {
      return ERC20__factory.connect(tokenAddr, signer);
    }
    return undefined;
  }, [signer, tokenAddr]);
}

export function useTestNetErc20Contract(
  tokenAddr: string | undefined,
  provider: JsonRpcProvider | undefined,
  account: string | null | undefined,
  chainId: CHAIN_ID | undefined,
): ERC20 | undefined {
  const providerOrSigner = useProviderOrSigner(provider, account);
  return useMemo(() => {
    if (isTestnet(chainId)) {
      if (providerOrSigner && tokenAddr) {
        return ERC20__factory.connect(tokenAddr, providerOrSigner);
      }
    }

    return undefined;
  }, [chainId, providerOrSigner, tokenAddr]);
}

export function useInstrumentContract(
  instrumentAddr: string | undefined,
  signer: JsonRpcSigner | undefined,
): Instrument | undefined {
  return useMemo(() => {
    if (signer && instrumentAddr) {
      return Instrument__factory.connect(instrumentAddr, signer);
    }

    return undefined;
  }, [instrumentAddr, signer]);
}
